import React from 'react';
import { format } from 'date-fns';
import { Button, Box, MenuItem, Paper, Select, SelectChangeEvent, TablePagination } from '@mui/material';

import {
    LinkStatus,
    linkStatusOptions,
    useTrackingLinksTableData
} from 'components/tracking-links/hooks/useTrackingLinksTableData';
import { MuiGrid } from 'lib/MuiGrid/MuiGrid';
import { GridStorageName } from 'lib/MuiGrid/StateController';
import { DownloadQRModal } from 'components/tracking-links/components/DownloadQRModal';
import { CreateTrackingLinkForm } from 'components/tracking-links/components/CreateTrackingLinkForm';
import { ActionDialog } from 'ui/dialogs/ActionDialog';
import { Location } from 'components/location/models/LocationModel';

type TrackingLinksTableProps = {
    hasLinksWritePermission: boolean;
    locations: Location[];
    utmParametersEnabled: boolean;
    appLinksLimitReached: boolean;
    onAppLinkCreated: () => void;
};

export const TrackingLinksTable = ({
    hasLinksWritePermission,
    locations,
    utmParametersEnabled,
    appLinksLimitReached,
    onAppLinkCreated
}: TrackingLinksTableProps) => {
    const {
        columns,
        pagedLinks,
        loading,
        selectedRow,
        handleSelectRow,
        page,
        onPageChange,
        count,
        rowsPerPage,
        status,
        handleStatusChange,
        isLinkSelected,
        selectedLink,
        isSelectedLinkValid,
        isSelectedLinkArchived,
        selectedShortLinkUrl,
        createLinkModalOpen,
        downloadQRModalOpen,
        archiveDialogOpen,
        archiveLoading,
        activateDialogOpen,
        activateLoading,
        handleOpenCreateLinkModal,
        handleCloseCreateLinkModal,
        handleCreateLinkSubmit,
        handleViewLink,
        handleDownloadQR,
        handleCopyLink,
        handleArchiveLink,
        handleCloseArchiveDialog,
        handleConfirmArchive,
        handleActivateLink,
        handleCloseActivateDialog,
        handleConfirmActivate,
        handleCloseDownloadQRModal
    } = useTrackingLinksTableData();

    const rows = React.useMemo(
        () =>
            pagedLinks.map(link => ({ ...link, createdAt: format(new Date(link.createdAt), 'dd/MM/yyyy') })),
        [pagedLinks]
    );

    const Pagination = React.useCallback(
        () => (
            <TablePagination
                count={count}
                onPageChange={onPageChange}
                page={page}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[rowsPerPage]}
                component="div"
                sx={{
                    '&:last-child': {
                        paddingRight: 6
                    }
                }}
            />
        ),
        [onPageChange, page, count, rowsPerPage]
    );

    const handleStatusSelectChange = React.useCallback(
        (e: SelectChangeEvent<LinkStatus>) => {
            handleStatusChange(e.target.value as LinkStatus);
        },
        [handleStatusChange]
    );

    return (
        <Box display="flex" flexDirection="column" gap={2} flex={1} minHeight={0}>
            <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
                <Box>
                    <Select
                        value={status}
                        onChange={handleStatusSelectChange}
                        variant="outlined"
                        size="small"
                        sx={{ minWidth: 200 }}
                    >
                        {linkStatusOptions.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
                <Box display="flex" justifyContent="flex-end" gap={1}>
                    {hasLinksWritePermission && (
                        <Button
                            variant="contained"
                            size="small"
                            color="primary"
                            onClick={handleOpenCreateLinkModal}
                        >
                            Create Link
                        </Button>
                    )}
                    <Button
                        disabled={!isLinkSelected}
                        variant="outlined"
                        size="small"
                        color="primary"
                        onClick={handleViewLink}
                    >
                        View
                    </Button>
                    <Button
                        disabled={!isLinkSelected || !isSelectedLinkValid}
                        variant="outlined"
                        size="small"
                        color="primary"
                        onClick={handleDownloadQR}
                    >
                        Download QR
                    </Button>
                    <Button
                        disabled={!isLinkSelected || !isSelectedLinkValid}
                        variant="outlined"
                        size="small"
                        color="primary"
                        onClick={handleCopyLink}
                    >
                        Copy Link
                    </Button>
                    {!isSelectedLinkArchived && hasLinksWritePermission && (
                        <Button
                            disabled={!isLinkSelected || !isSelectedLinkValid}
                            variant="outlined"
                            size="small"
                            color="primary"
                            onClick={handleArchiveLink}
                        >
                            Archive
                        </Button>
                    )}
                    {isSelectedLinkArchived && hasLinksWritePermission && (
                        <Button variant="outlined" size="small" color="primary" onClick={handleActivateLink}>
                            Activate
                        </Button>
                    )}
                </Box>
            </Box>

            <Paper sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
                <MuiGrid
                    columns={columns}
                    rows={rows}
                    loading={loading}
                    storageName={GridStorageName.Links}
                    rowSelectionModel={selectedRow}
                    hideFooter={false}
                    disableMultipleRowSelection
                    onRowSelectionModelChange={handleSelectRow}
                    disableRowSelectionOnClick={false}
                    rowHeight={40}
                    rowCount={count}
                    pagination
                    paginationMode="server"
                    slots={{
                        pagination: Pagination
                    }}
                />
            </Paper>
            <DownloadQRModal
                open={downloadQRModalOpen && isSelectedLinkValid}
                link={selectedLink}
                shortLinkUrl={selectedShortLinkUrl}
                onClose={handleCloseDownloadQRModal}
            />
            <ActionDialog
                open={archiveDialogOpen}
                title={`Archive "${selectedLink?.internalTitle ?? ''}"?`}
                content={
                    <>
                        Archived links will still track clicks. You can view them by filtering the page to
                        view archived links. If you need to permanently delete and deactivate a link, please
                        get in touch with support.
                    </>
                }
                submitLabel="Archive"
                loading={archiveLoading}
                onConfirm={handleConfirmArchive}
                onClose={handleCloseArchiveDialog}
            />
            <ActionDialog
                open={activateDialogOpen}
                title={`Activate "${selectedLink?.internalTitle ?? ''}"?`}
                content="Activating this link will reactivate it and make it visible under the 'Active' category."
                submitLabel="Activate"
                loading={activateLoading}
                onConfirm={handleConfirmActivate}
                onClose={handleCloseActivateDialog}
            />
            <CreateTrackingLinkForm
                open={createLinkModalOpen}
                locations={locations}
                utmParametersEnabled={utmParametersEnabled}
                appLinksLimitReached={appLinksLimitReached}
                onAppLinkCreated={onAppLinkCreated}
                onClose={handleCloseCreateLinkModal}
                onSubmit={handleCreateLinkSubmit}
            />
        </Box>
    );
};
